/**
 * Dashboard page component
 *
 * @author Claude
 */
import { useState, useEffect } from 'react';
import {
  Typography,
  Grid,
  Box,
  Button,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';
import { ClientCard } from '../components/features/ClientCard';
import { useAuth } from '../hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import { getClients } from '../services/clientService';
import { ClientFormValues, ClientSummary } from '../types/client';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { FormTextField } from '../components/common/FormTextField';
import { Helmet } from 'react-helmet-async';
import { SearchBar } from '../components/common/SearchBar';
import { useSearch } from '../hooks/useSearch';
import { useNotification } from '../hooks/useNotification';

// Client form validation schema
const clientSchema = Yup.object({
  name: Yup.string()
    .required('Client name is required')
    .max(100, 'Client name must be less than 100 characters'),
  description: Yup.string().max(500, 'Description must be less than 500 characters'),
  contactName: Yup.string().max(100, 'Contact name must be less than 100 characters'),
  contactTitle: Yup.string().max(100, 'Contact title must be less than 100 characters'),
  contactEmail: Yup.string()
    .email('Please enter a valid email address')
    .max(255, 'Email must be less than 255 characters'),
  contactPhone: Yup.string().max(50, 'Phone number must be less than 50 characters'),
  sharepointLink: Yup.string()
    .max(500, 'Link must be less than 500 characters')
    .test('is-url', 'Please enter a valid URL (e.g. https://example.com)', value => {
      if (!value) return true;
      try {
        new URL(value);
        return true;
      } catch {
        return false;
      }
    }),
});

/**
 * Dashboard page showing client cards
 * @returns {JSX.Element} Dashboard page component
 *
 * @a11y Page has proper focus management
 * @a11y Uses proper heading hierarchy
 * @a11y Loading and error states are announced to screen readers
 */
export const Dashboard = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
  const isClientViewer = user?.role === 'client_viewer';

  const [clients, setClients] = useState<ClientSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAddDialog, setShowAddDialog] = useState(false);

  const { query, setQuery, getSearchParams } = useSearch({
    defaultQuery: '',
    syncWithUrl: true,
  });
  const { showSuccess, showError } = useNotification();

  // Default empty form values
  const initialFormValues: ClientFormValues = {
    name: '',
    description: '',
    contactName: '',
    contactTitle: '',
    contactEmail: '',
    contactPhone: '',
    sharepointLink: '',
  };

  // Load clients on mount and when search changes
  useEffect(() => {
    fetchClients();
  }, [token, query]);

  // Fetch clients from API
  const fetchClients = async () => {
    if (!token) return;

    try {
      setLoading(true);
      setError(null);

      const searchParams = getSearchParams();
      const response = await getClients(
        token,
        100, // limit
        0, // offset
        'name', // order
        'asc', // dir
        searchParams.q, // search query
        { expand: 'true' } // include reports for latestReportDate
      );

      // Use the clients directly from the API response
      const clientsWithSummary: ClientSummary[] = response.clients;

      setClients(clientsWithSummary);
    } catch (err: any) {
      console.error('Failed to fetch clients:', err);
      setError(err.message || 'Failed to load clients. Please try again.');
      showError(err.message || 'Failed to load clients. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Open add client dialog
  const handleOpenAddDialog = () => {
    setShowAddDialog(true);
  };

  // Close add client dialog
  const handleCloseAddDialog = () => {
    setShowAddDialog(false);
  };

  // Handle form submission for new client
  const handleSubmit = async (values: ClientFormValues, { setSubmitting, resetForm }: any) => {
    if (!token) {
      showError('You must be logged in to create a client');
      return;
    }

    try {
      // Actually create the client via API
      const { createClient } = await import('../services/clientService');
      await createClient(token, values);

      // Close dialog and reset form
      handleCloseAddDialog();
      resetForm();

      // Refresh client list
      await fetchClients();

      // Show success notification
      showSuccess('Client created successfully!');
    } catch (err: any) {
      console.error('Failed to create client:', err);
      showError(err.message || 'Failed to create client. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Dashboard - Accessibility Maturity Reporter</title>
      </Helmet>

      <Box sx={{ width: '100%', mb: 4 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 3,
            flexWrap: 'wrap',
            gap: 2,
          }}
        >
          <Typography component="h1" variant="h4">
            Dashboard
          </Typography>

          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              startIcon={loading ? <CircularProgress size={16} /> : <RefreshIcon />}
              onClick={fetchClients}
              disabled={loading}
              aria-label="Refresh"
            >
              Refresh
            </Button>

            {!isClientViewer && (
              <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenAddDialog}>
                Add Client
              </Button>
            )}
          </Box>
        </Box>

        {loading && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              p: 4,
            }}
            aria-live="polite"
          >
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} role="alert">
            {error}
          </Alert>
        )}

        {!loading && !error && clients.length === 0 && (
          <Alert severity="info" sx={{ mb: 3 }}>
            No clients found. Add your first client to get started.
          </Alert>
        )}

        <Grid container spacing={3}>
          {clients.map(client => (
            <Grid item xs={12} sm={6} md={4} key={client.id}>
              <ClientCard client={client} />
            </Grid>
          ))}

          {/* "Add New Client" card */}
          {!isClientViewer && (
            <Grid item xs={12} sm={6} md={4}>
              <ClientCard isAddCard onAddClick={handleOpenAddDialog} />
            </Grid>
          )}
        </Grid>
      </Box>

      {/* Add Client Dialog */}
      <Dialog
        open={showAddDialog}
        onClose={handleCloseAddDialog}
        fullScreen={fullScreen}
        maxWidth="md"
        aria-labelledby="add-client-dialog-title"
      >
        <DialogTitle id="add-client-dialog-title">
          Add New Client
          <IconButton
            aria-label="close"
            onClick={handleCloseAddDialog}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <Formik
          initialValues={initialFormValues}
          validationSchema={clientSchema}
          onSubmit={handleSubmit}
        >
          {({ isSubmitting }) => (
            <Form>
              <DialogContent dividers>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <FormTextField name="name" label="Client Name" required autoFocus />
                  </Grid>

                  <Grid item xs={12}>
                    <FormTextField name="description" label="Description" multiline rows={3} />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <FormTextField name="contactName" label="Contact Name" />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <FormTextField name="contactTitle" label="Contact Title" />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <FormTextField name="contactEmail" label="Contact Email" type="email" />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <FormTextField name="contactPhone" label="Contact Phone" />
                  </Grid>

                  <Grid item xs={12}>
                    <FormTextField
                      name="sharepointLink"
                      label="Client Drive Link"
                      type="text"
                      placeholder="https://drive.google.com/... or https://company.sharepoint.com/..."
                    />
                  </Grid>
                </Grid>
              </DialogContent>

              <DialogActions>
                <Button onClick={handleCloseAddDialog} color="inherit">
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={isSubmitting}
                  startIcon={isSubmitting ? <CircularProgress size={16} /> : null}
                >
                  {isSubmitting ? 'Saving...' : 'Save Client'}
                </Button>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>
    </>
  );
};
